import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CreerUtilisateurTenantSSOModel, CreerUtilisateurTenantSSOItem } from 'src/models/pilotage/creerUtilisateurTenantSSOModel';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { tap } from 'rxjs';
import * as XLSX from 'xlsx';

import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { ContexteUtilisateur, EtiquetteModel } from 'src/models/pilotage/contextePilotageUtilisateurModel';
import { EtiquetteService } from 'src/services/etiquette.service';
import { ConfirmationCreationUtilisateurDialogComponent } from '../confirmation-creation-utilisateur-dialog/confirmation-creation-utilisateur-dialog.component';
import { ResultCreerUtilisateursTenantSSOModel } from 'src/models/pilotage/resultCreerUtilisateurTenantSSOModel';

@Component({
    selector: 'app-creer-utilisateur-tenant-sso-dialog',
    templateUrl: './creer-utilisateur-tenant-sso-dialog.component.html',
    styleUrls: ['./creer-utilisateur-tenant-sso-dialog.component.scss'],
    imports: [
    FormsModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    TranslateModule
]
})
export class CreerUtilisateurTenantSSODialogComponent implements OnInit {
    entiteSelectionnee: number = 0;
    entites: any[] = [];
    entiteUnique: { id: number, nom: string } | null = null;
    utilisateurManuel: CreerUtilisateurTenantSSOItem = { prenom: '', nom: '', courriel: '' };
    fichierSelectionne: File | null = null;
    methodeCreation: 'excel' | 'manual' = 'manual';
    utilisateursExcel: CreerUtilisateurTenantSSOItem[] = [];
    chargementEnCours: boolean = false;

    etiquettesDisponibles: EtiquetteModel[] = [];
    etiquettesSelectionnees: EtiquetteModel[] = [];

    constructor(
        private dialogRef: MatDialogRef<CreerUtilisateurTenantSSODialogComponent>,
        private utilisateurService: UtilisateurService,
        public etiquetteService: EtiquetteService,
        private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.chargerEntites();
    }

    chargerEntites(): void {
        this.entites = this.utilisateurService.contexteUtilisateur.entitesAccessibles
            .filter(re => !re.estGroupe)
            .map((e: any) => ({ id: e.entiteId, nom: e.nom }));

        if (this.entites.length === 1) {
            this.entiteUnique = this.entites[0];
            this.entiteSelectionnee = this.entiteUnique!.id;
        } else {
            if (this.utilisateurService.entiteUtilisateurSelectionnee) {
                this.entiteSelectionnee = this.utilisateurService.entiteUtilisateurSelectionnee.entiteId;
            }
        }
        this.mettreAJourEtiquettesDisponibles();
    }

    mettreAJourEtiquettesDisponibles(): void {
        const groupEntites = this.utilisateurService.contexteUtilisateur.toutesLesEntites
            .filter(e => e.estGroupe)
            .map(e => e.entiteId);

        const etiquettesGroupe = this.utilisateurService.contexteUtilisateur.toutesLesEtiquettes
            .filter(e => e.entiteIds.some(id => groupEntites.includes(id)));

        const etiquettesEntite = this.utilisateurService.contexteUtilisateur.toutesLesEtiquettes
            .filter(e => e.entiteIds.includes(this.entiteSelectionnee));

        const nouvellesEtiquettesAccessibles = [...new Set([...etiquettesGroupe, ...etiquettesEntite])];

        this.etiquettesSelectionnees = this.etiquettesSelectionnees.filter(etiquette =>
            nouvellesEtiquettesAccessibles.some(etiquetteAccessible => etiquetteAccessible.etiquetteId === etiquette.etiquetteId)
        );

        this.etiquettesDisponibles = nouvellesEtiquettesAccessibles
            .filter(etiquette => !this.etiquettesSelectionnees.some(selected => selected.etiquetteId === etiquette.etiquetteId))
            .sort((a, b) =>
                this.etiquetteService.getNomLocalise(a.locales).localeCompare(
                    this.etiquetteService.getNomLocalise(b.locales)
                )
            );
    }

    onEtiquetteSelectionnee(event: Event, selectElement: HTMLSelectElement): void {
        const etiquetteId = Number(selectElement.value);
        const etiquetteSelectionnee = this.etiquettesDisponibles.find(e => e.etiquetteId === etiquetteId);
        if (etiquetteSelectionnee) {
            this.etiquettesSelectionnees.push(etiquetteSelectionnee);
            this.etiquettesDisponibles = this.etiquettesDisponibles.filter(e => e.etiquetteId !== etiquetteId);
            selectElement.value = '';
        }
    }

    supprimerEtiquetteSelectionnee(id: number): void {
        const removedEtiquette = this.etiquettesSelectionnees.find(e => e.etiquetteId === id);
        this.etiquettesSelectionnees = this.etiquettesSelectionnees.filter(e => e.etiquetteId !== id);
        if (removedEtiquette) {
            this.etiquettesDisponibles.push(removedEtiquette);
            this.etiquettesDisponibles.sort((a, b) =>
                this.etiquetteService.getNomLocalise(a.locales).localeCompare(
                    this.etiquetteService.getNomLocalise(b.locales)
                )
            );
        }
    }

    telechargerModeleExcel(): void {
        this.utilisateurService.obtenirModeleUtilisateurTenantSSO().pipe(
            tap(
                (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'modeleTenant.xlsx';
                    link.click();
                    window.URL.revokeObjectURL(url);
                },
                (error: any) => {
                    console.error('Error downloading file:', error);
                }
            )
        ).subscribe();
    }

    onFichierSelectionne(event: any) {
        const file = event.target.files[0];
        this.fichierSelectionne = file;
        const reader = new FileReader();

        reader.onload = (e: any) => {
            const data = e.target.result;
            const workbook = XLSX.read(data, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const excelData = XLSX.utils.sheet_to_json(worksheet);

            this.utilisateursExcel = excelData
                .map((row: any) => ({
                    prenom: row.Prenom,
                    nom: row.Nom,
                    courriel: row.Courriel || row.Email
                }))
                .filter(user => user.prenom && user.nom && user.courriel);
        };

        reader.readAsBinaryString(file);
    }

    boutonCreerDesactive(): boolean {
        if (this.entiteSelectionnee === 0) {
            return true;
        }

        if (this.methodeCreation === 'manual') {
            return !this.utilisateurManuel.prenom?.trim() || !this.utilisateurManuel.nom?.trim() || !this.utilisateurManuel.courriel?.trim();
        }

        if (this.methodeCreation === 'excel') {
            return this.fichierSelectionne === null || this.utilisateursExcel.length === 0;
        }
        return false;
    }

    private readonly TAILLE_LOT = 5;

    onAnnuler(): void {
        this.dialogRef.close(false);
    }

    onConfirmer(): void {
        this.chargementEnCours = true;

        const utilisateurs = this.methodeCreation === 'manual'
            ? [this.utilisateurManuel]
            : this.utilisateursExcel;

        const lots = this.decoupeEnLots(utilisateurs, this.TAILLE_LOT);
        const resultAggrege: ResultCreerUtilisateursTenantSSOModel = {
            utilisateursSucces: [],
            utilisateursDejaPresents: [],
            utilisateursEnErreurs: []
        };

        this.envoyerLot(0, lots, resultAggrege);
    }

    private envoyerLot(index: number, lots: CreerUtilisateurTenantSSOItem[][], resultAggrege: ResultCreerUtilisateursTenantSSOModel): void {
        if (index >= lots.length) {
            this.chargementEnCours = false;
            this.dialogRef.close(true);
            this.dialog.open(ConfirmationCreationUtilisateurDialogComponent, { data: resultAggrege });
            return;
        }

        const model: CreerUtilisateurTenantSSOModel = {
            entiteId: this.entiteSelectionnee,
            utilisateurs: lots[index],
            etiquetteIds: this.etiquettesSelectionnees.map(e => e.etiquetteId),
            codeLangue: this.utilisateurService.obtenirLangueCourante()
        };

        this.utilisateurService.creerUtilisateursTenantSSO(model).subscribe({
            next: (result) => {
                resultAggrege.utilisateursSucces.push(...result.utilisateursSucces);
                resultAggrege.utilisateursDejaPresents.push(...result.utilisateursDejaPresents);
                resultAggrege.utilisateursEnErreurs.push(...result.utilisateursEnErreurs);
                this.envoyerLot(index + 1, lots, resultAggrege);
            },
            error: () => {
                this.chargementEnCours = false;
                this.dialogRef.close(false);
            }
        });
    }

    private decoupeEnLots<T>(items: T[], taille: number): T[][] {
        const lots: T[][] = [];
        for (let i = 0; i < items.length; i += taille) {
            lots.push(items.slice(i, i + taille));
        }
        return lots;
    }

    onEntiteSelectionnee(event: any): void {
        this.entiteSelectionnee = parseInt(event.target.value);
        this.mettreAJourEtiquettesDisponibles();
    }
}
